import {StudentGroup, StudentStore} from "Stores/studentStore";
import {AppStore} from "Stores/appStore";
import * as React from "react";
import {Utils, Defaults} from "../../common/utils";
import {Alert, Spinner } from "react-bootstrap";
import {inject, observer} from "mobx-react";
import {CommonStore, Student, StudentsDataQuery} from "Stores/commonStore";
import {
    CellContext,
    ColumnDef,
    PaginationState,
    SortingState,
    Row,
    ExpandedState,
} from "@tanstack/react-table";
import {useEffect, useMemo, useState} from "react";
import {StudentForm} from "Components/Students/StudentForm";
import {SimpleCheckbox} from "Components/Common/SimpleCheckbox";
import { StyledTable } from "Components/Common/StyledTable";
import DebouncedInput from "Components/Common/DebouncedInput";
import ArrowUpIcon from "Components/Icons/ArrowUpIcon";
import ArrowDownIcon from "Components/Icons/ArrowDownIcon";
import PageTitle from "Components/Common/PageTitle";

interface StudentProps {
    studentStore?: StudentStore
    commonStore?: CommonStore
    appStore?: AppStore
}

interface StudentState {
    students: Student[]
    loading: boolean
    pageSize: number
}

@inject("studentStore", "commonStore", "appStore")
@observer
export class StudentManagement extends React.Component<StudentProps, StudentState> {

    state = {
        loading: false,
        students: [] as Student[],
        pageSize: Defaults.tablePageSize(),
    };

    componentDidMount() {
        Utils.setViewName("StudentList", "Students");
    }

    render() {
        if (!this.props.studentStore || !this.props.commonStore || !this.props.appStore) {
            return null;
        }

        if (!Utils.hasAdminAccess()) {
            return (
                <div className="col-sm-12col col-md-12">
                    <Alert key="Student-Warning" variant="warning">
                        {`Sorry, you don't have access to student management. If you think this is an error,\n
                            please contact your administrator so he/she can give you the correct access level.`}
                    </Alert>
                </div>
            );
        }
        return (
            <>
                <PageTitle pageTitle={"Students"} />
                <StudentsManagementPage commonStore={this.props.commonStore} appStore={this.props.appStore} studentStore={this.props.studentStore}/>
            </>
        );
    }
}

const getPageCount = (totalCount: number, pageSize: number): number => {
    return Math.ceil(totalCount/pageSize) || 0;
};

interface StudentsManagementPageProps {
    commonStore: CommonStore
    appStore: AppStore
    studentStore: StudentStore
}

function StudentsManagementPage({commonStore, studentStore}: StudentsManagementPageProps) {
    const [searchBarValue, setSearchBarValue] = useState("");
    const [dataQuery, setDataQuery] = useState<StudentsDataQuery | null>(null);
    const [sorting, setSorting] = useState<SortingState>([]);
    const [studentGroups, setStudentGroups] = useState<StudentGroup[]>([]);
    const [refreshKey, setRefreshKey] = useState(0);
    const [expanded, setExpanded] = useState<ExpandedState>({});
    const [{ pageIndex, pageSize }, setPagination] =
        useState<PaginationState>({
            pageIndex: 0,
            pageSize: Defaults.tablePageSize(),
        });

    const columns = useMemo<ColumnDef<Student>[]>(
        () => [
            {
                header: () => {},
                id: "enabled",
                enableColumnFilter: false,
                cell: props => renderEnabledCheckBox(props.row.original)
            },
            {
                header: "First Name",
                accessorKey: "first_name",
            },
            {
                header: "Last Name",
                accessorKey: "last_name",
            },
            {
                header: "Student ID",
                accessorKey: "schools_student_id",
            },
            {
                header: "Email",
                accessorKey: "student_email",
            },
            {
                header: "Academic Status",
                accessorKey: "status",
                cell: (props: CellContext<Student, unknown>) => {
                    return (
                        <div className={`status-column ${props.row.original.status.toLowerCase()}`}>
                            {props.row.original.status}
                        </div>
                    );
                }
            },
            {
                id: "expander",
                header: "Actions",
                enableColumnFilter: false,
                cell: (props: CellContext<Student, unknown>) => {
                    return (
                        <div className="actions-column">
                            <i className="icon icon-cog" />
                            Settings
                            <div className="vl"></div>
                            <button
                                className="table-action-button"
                                data-test-id={`student-toggle-${props.row.original.ruuid}`}
                                onClick={props.row.getToggleExpandedHandler()}>
                                {props.row.getIsExpanded() ?
                                    <ArrowUpIcon /> : <ArrowDownIcon />
                                }
                            </button>
                        </div>
                    );
                },
            },
        ], []);

    useEffect(() => {
        async function fetchStudents() {
            let sortBy;
            let sortOrder;
            if (sorting.length > 0) {
                sortBy = sorting[0].id;
                sortOrder = sorting[0].desc ? "desc" : "asc";
            }

            const filters = {};

            if (searchBarValue) {
                filters["search_term"] = {
                    operator: "contains",
                    value: `${searchBarValue}`,
                };
            }

            await commonStore.fetchStudents({page: pageIndex + 1, pageSize, sortBy, sortOrder, include_disabled: true, filters});
            setDataQuery({
                data: commonStore.students,
                result: {totalCount: commonStore.totalStudents}
            });
        }

        fetchStudents().catch(() => {});
    }, [pageIndex, refreshKey, pageSize, sorting, searchBarValue]);

    useEffect(() => {
        async function fetchStudentGroups() {
            await studentStore.fetchStudentGroups();
            setStudentGroups(studentStore.studentGroups);
        }
        fetchStudentGroups().catch(() => {});
    }, []);

    const toggleDisabledStudent = async (student: Student) => {
        await studentStore?.disabledStudent(student.ruuid, !student.disabled);
        setRefreshKey(prevState => prevState + 1);
     };

    const renderEnabledCheckBox = (student: Student) => {
        return (
            <div className="active-labels-checkbox">
                <SimpleCheckbox
                    id={"student-form-enabled-" + student.ruuid} isChecked={!student.disabled}
                    onChange={() =>
                    {
                        toggleDisabledStudent(student);
                    }} />
            </div>
        );
    };

    const getEditHandler = (row: Row<Student>) => {
        return async (studentRuuid: string, groups: string[]) => {
            await studentStore.addGroupsToStudent(studentRuuid, groups);
            setRefreshKey(prevState => prevState + 1);
            row.toggleExpanded();
        };
    };


    const renderSubComponent = ({ row }: { row: Row<Student> }) => {
        return (
            <div className={"student-form-container"}>
                <StudentForm
                    onSubmit={getEditHandler(row)}
                    onCancel={() => row.toggleExpanded()}
                    student={row.original}
                    groups={studentGroups}
                />
            </div>
        );
    };

    const renderStudentsTable = () => {
        return (
            <div className="students-main-container">
                <div className="header-students">
                    <h1 className="title-students">Students</h1>
                </div>
                <div className="search-bar">
                    <DebouncedInput
                        type="text"
                        value={searchBarValue}
                        onChange={(value: string) => {
                            setPagination((prev) => ({...prev, pageIndex: 0}));
                            setSearchBarValue(value.toString());
                        }}
                        placeholder="Search by Name, ID, Email, Status or Group name"
                        className="search-input"
                        data-test-id="students-search"
                    />
                    <i className={"type icon icon-search search-icon-bar"} aria-hidden="true" data-testid="icon-search"></i>
                </div>
                {!dataQuery?.data ? <div className="text-center"><Spinner variant="success" /></div> :
                    <StyledTable
                        defaultData={dataQuery?.data ?? []}
                        columns={columns}
                        getRowCanExpand={(value) => Boolean(value)}
                        expanded={expanded}
                        setExpanded={setExpanded}
                        onIsExpanded={(row) => {
                          return (
                            <div>
                                {renderSubComponent({row})}
                            </div>
                          );
                        }}
                        className="students-table"
                        noDataMessage="No students were found"
                        currentPageNumber={pageIndex}
                        pageSize={pageSize}
                        totalPagesNumber={getPageCount(dataQuery?.result?.totalCount ?? 0, pageSize)}
                        setCurrentNumber={(e) => setPagination({pageIndex: e, pageSize})}
                        setPageSize={(e) => setPagination({pageIndex, pageSize: e})}
                        setSorting={(e) => {setSorting(e);}}
                        sorting={sorting}
                    />
                }
            </div>
        );
    };

    return (
        <>
            <div className="students-container" >
                { renderStudentsTable() }
            </div>
        </>
    );
}
